import React, { useState } from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Lollipop from 'components/common/Lollipop/XaxisLollipop/lollipop';
import lollipopClasses from 'components/common/Lollipop/XaxisLollipop/lollipop.module.scss';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import Slider, { marks } from 'components/common/Slider/Slider';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer } from 'components/common';
import { useHighLevelProgramLogic } from 'components/widgets/HighLevelProgram/HighLevelProgram.logic';
import classes from 'components/widgets/HighLevelProgram/HighLevelProgram.module.scss';
import { useHighLevelProgramProvider } from 'components/widgets/HighLevelProgram/HighLevelProgram.provider';
import { highLevelFilterInitialState, serialize } from 'components/widgets/HighLevelProgram/HighLevelProgram.utils';

const { componentKey, id, endpoint } = widgetsConfigurations.highLevelProgramLatest;

export const HighLevelProgram = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.highLevelProgramLatest.title,
    maxLevel: maxLevelFrombLO,
}: IComponentProps) => {
    // Local State
    const [maxLevel, setMaxLevel] = useState<number[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? (localFilters?.maxLevel as number[])
            : highLevelFilterInitialState,
    );

    // Provider
    const { ui, api } = useHighLevelProgramProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        maxLevel,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.highLevelProgram.payload,
        widgetEndpoint: endpoint,
    });

    // Logic
    const { logic } = useHighLevelProgramLogic({ api, setMaxLevel });

    return (
        <ErrorBoundary>
            <WidgetExistInBackend componentKey={componentKey}>
                <WidgetWithTitle
                    id={id}
                    title={
                        <WidgetTitle
                            title={editableTitle}
                            setTitle={setEditableTitle}
                            widgetViewMode={widgetViewMode}
                        />
                    }
                    className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                    titleComponents={[
                        !widgetId && (
                            <div key={'SliderWrapper'} className={classes.selectWrapper}>
                                <div className={classes.title}>Max Level</div>
                                <Slider
                                    key={'Slider'}
                                    defaultValue={highLevelFilterInitialState}
                                    step={1}
                                    min={1}
                                    max={maxLevelFrombLO}
                                    marks={marks(1, maxLevelFrombLO)}
                                    onChangeCommitted={(e, value) => logic.handleHighLevelFilter(value)}
                                    sx={{ width: '20rem' }}
                                />
                            </div>
                        ),
                        externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                        <AddWidget
                            key={'AddWidget'}
                            componentKey={componentKey}
                            title={title}
                            projectId={projectId}
                            contractId={contractId}
                            widgetId={widgetId}
                            route={route}
                            localFilters={ui.state}
                            shouldUseNewIcon={true}
                            shouldHide={true}
                        />,
                        <AddToReport
                            key={'addWidgetToReport'}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            projectId={projectId}
                            contractId={contractId}
                            uiState={ui.state}
                            shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                            apiPayload={api.highLevelProgram.payload}
                            widgetEndpoint={endpoint}
                        />,
                        !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                            <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                        ),
                        <TooltipIcon key={'info'} tooltip={api.highLevelProgram.instance.data?.info} />,
                        !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                            <ThreeDotsMenu
                                widgetViewMode={widgetViewMode}
                                widgetId={widgetId}
                                componentKey={componentKey}
                                shareTitle={title}
                                shareLink={route}
                            />
                        ),
                    ]}
                    titleFilters={[
                        <ReportingVersionAndPills
                            key={'ReportingVersionAndPills'}
                            selectedProjectAndContract={widgetSelectedProjectAndContract}
                            pills={ui.pills}
                            widgetViewMode={widgetViewMode}
                        />,
                        <Narrative key={'narrative'} editNarrative={editNarrative} />,
                        <PinboardPills
                            key={'pinboardPills'}
                            isInPinboard={ui.isInPinboard}
                            pills={ui.pills}
                            widgetViewMode={widgetViewMode}
                        />,
                    ]}
                >
                    {api.highLevelProgram.instance.data?.data &&
                        api.highLevelProgram.instance.data?.data.length > 0 && (
                            <div className={classes.chartContainer}>
                                <Lollipop
                                    dataset={serialize(api.highLevelProgram.instance.data.data)}
                                    nowDate={api.highLevelProgram.instance.data?.nowDate}
                                    filter={maxLevel}
                                    className={
                                        WidgetViewModeUtils.isReportView(widgetViewMode)
                                            ? lollipopClasses.reportMode
                                            : ''
                                    }
                                />
                            </div>
                        )}
                    {api.highLevelProgram.instance.isLoading && !api.highLevelProgram.instance.data && (
                        <LoaderContainer>
                            <OverlayWithSpinner />
                        </LoaderContainer>
                    )}
                    {!api.highLevelProgram.instance.isLoading &&
                        (api.highLevelProgram.instance.data?.data === null ||
                            api.highLevelProgram.instance.data?.data.length === 0) && (
                            <NoData>{logic.noDataText}</NoData>
                        )}
                </WidgetWithTitle>
            </WidgetExistInBackend>
        </ErrorBoundary>
    );
};
